import Vue, { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'

import { H6, Overline, Subtitle2 } from '@src/elements/typography/typography'
import { v1 as uuidv1 } from 'uuid'

import { Card } from '../../card'
import { ExerciseInfoModal } from '@src/components/exercise-info/exercise-info'
import { Icon } from '@src/elements/icon/icon'
import { Radio } from '@src/elements/input/radio'
import { PositionChanger } from '@src/elements/position-changer/position-changer'
import { Input } from '@src/elements/input/input'
import EventBus from '@src/lib/event_bus'
import Club from '@src/data/club'
import { WorkoutExercise } from '@src/data/workout-exercise'
import { WorkoutType } from '@src/data/workout-data'
import { ALLOWED_TEMPO_ADJUSTMENT_PERCENTAGE } from '@src/util/constants'
import { Tooltip } from '@src/elements/tooltip/tooltip'
import { ExerciseResponse } from '@src/data/exercise-response'
import { ExercisePreview } from '../exercise-preview/exercise-preview'
import { HealthLimitationSet } from '@src/data/health-limitation-set'
import { healthCheck } from '@src/util/functions'
import { ExerciseTag } from '@src/util/default-values'
import { exerciseName } from '@src/util/exercise-name'

import '../exercise-card.sass'

const MAXIMUM_EXERCISE_PAUSE = 100 // 100 seconds

@Component({
  computed: {
    isCircleWorkout () {
      return this.$store.state.new_workout.circleWorkout
    },
    isContinuousWorkout () {
      return this.$store.state.new_workout.continuousWorkout
    },
    workoutType () {
      return this.$store.state.new_workout.type
    },
    selectedWorkoutExercises () {
      return this.$store.state.new_workout.workoutExercises
    },
    exercises () {
      return this.$store.state.availableExercises
    },
    weightUnit () {
      return this.$store.state.club.weightUnit.toUpperCase()
    },
    exTags () {
      return this.$store.state.exerciseTags
    }
  }
})
export class ExerciseCardConfig extends Vue {

  @Prop({ required: true })
  workoutExercise: WorkoutExercise

  @Prop({ required: false })
  club: Club

  @Prop({ required: true })
  index: number

  @Prop({ required: true })
  type: 'config' | 'default' | 'view' | 'strictView'

  @Prop({ required: false, default: false })
  showBreakpointOption: boolean

  @Prop({ required: false })
  private stationName: String

  @Prop({ required: false })
  private healthLimitations: HealthLimitationSet | null

  private isWeightConfigVisible: boolean = false
  private isTempoConfigVisible: boolean = false
  private isRepetitionsConfigVisible: boolean = false
  private isPauseConfigVisible: boolean = false
  private isSideConfigVisible: boolean = false
  private selectedWorkoutExercises: WorkoutExercise[]

  // if the workout is continuous, all configs are enabled (equipment, tempo, reps, pause, side)
  // if the workout is circle, the pause config cannot be edited
  private isCircleWorkout: boolean

  // if the workout is of type home, the pause between reps is a default one
  private workoutType: WorkoutType

  private showExerciseInfo: boolean = false

  private exercises: ExerciseResponse[]
  private exercise?: ExerciseResponse
  private exTags: ExerciseTag[]
  private weightUnit: string

  private addTooltip = false

  private minReps = 1
  private originalMinReps = 1
  private maxReps = 1000
  private originalMaxReps = 1000
  private defaultReps = '1'

  constructor (props) {
    super(props)
  }

  @Watch('workoutExercise.exercise.id', { immediate: true })
  getExerciseDetails () {
    this.exercise = this.exercises.find(ex => ex.id === this.workoutExercise.exercise.id) || this.workoutExercise.exercise
  }

  @Watch('selectedWorkoutExercises')
  selectedWorkoutExercisesChanged () {
    this.updateRepetitionsBounders()
  }

  mounted () {
    this.checkTitle()

    this.defaultReps = this.workoutExercise.getDefaultRepetitionsCount(this.club.screenExerciseLen).toFixed(0)
    this.updateRepetitionsBounders()
  }

  computeRepetitions () {
    const reps = Math.round(this.workoutExercise.getRepetitionsCount(this.club.screenExerciseLen))
    if (!this.isCircleWorkout && reps === 0) return 1
    return reps
  }

  updated () {
    this.checkTitle()
  }

  // check if title doesn't fit
  checkTitle () {
    let title = this.$refs.title as Vue
    this.addTooltip = title.$el.clientWidth < title.$el.scrollWidth
  }

  beforeMount () {
    EventBus.on('resetExerciseConfig', this.resetTiming)
    EventBus.on('setExerciseConfigRepetitions', this.setExerciseConfigRepetitions)
  }

  beforeDestroy () {
    EventBus.off('resetExerciseConfig', this.resetTiming)
    EventBus.off('setExerciseConfigRepetitions', this.setExerciseConfigRepetitions)
  }

  isEditable () {
    return this.type === 'config'
  }

  render (h: CreateElement) {
    const tempo = this.computeTempo()
    const isExpanded = this.isWeightConfigVisible || this.isTempoConfigVisible || this.isRepetitionsConfigVisible || this.isPauseConfigVisible || this.isSideConfigVisible

    return (
      <div class='card-wrapper'>
        <Card class={`exercise-card exercise-card-config ${isExpanded ? 'expanded' : ''}`}>
          {this.renderHeader(h)}
          {this.renderPreview(h)}
          {this.renderTitle(h)}
          {this.renderSpecs(h, tempo)}
          {this.renderRight(h)}

          {this.renderWeightConfig(h)}
          {this.renderTempoConfig(h, tempo)}
          {this.renderRepetitionsConfig(h)}
          {this.renderPauseConfig(h)}
          {this.renderSideConfig(h)}
        </Card>
        {this.renderExerciseBreakPoint(h)}
      </div>
    )
  }

  renderHeader (h: CreateElement) {
    return <Overline slot='header' class='header'>
      <Overline>
        {this.workoutExercise.exercise.is2DTracked() && this.workoutExercise.exercise.is3DTracked() ? this.$t('PAGE.CONTENT.WORKOUTS.TRACKED')
          : this.workoutExercise.exercise.is2DTracked() ? this.$t('PAGE.CONTENT.EXERCISES.2D_TRACKED')
            : this.workoutExercise.exercise.is3DTracked() ? this.$t('PAGE.CONTENT.EXERCISES.3D_TRACKED')
              : this.$t('PAGE.CONTENT.WORKOUTS.NON_TRACKED')} /&nbsp;</Overline>
      <Overline>ID {this.workoutExercise.exercise.number} </Overline>
      {this.workoutExercise.exercise.equipment.length > 0 &&
        <div>
          &nbsp;/&nbsp;
          {this.renderEquipmentIcon(h)}
        </div>
      }
      { this.stationName ?
        <Overline>&nbsp;/&nbsp;Station: {this.stationName}</Overline> : ''
      }
    </Overline>
  }

  renderEquipmentIcon (h: CreateElement) {
    const allEquipmentItems = this.workoutExercise.exercise.equipment.map((equipmentName) => {
      return this.renderEquipment(h, equipmentName)
    })
    return <div slot='specs' class='specs-equipment'>
      <div class='equipmentWrapper'>{allEquipmentItems}</div>
    </div>
  }

  renderEquipment (h: CreateElement, equipment: string) {
    return <div key={uuidv1()} class='equipment'>
      <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + equipment.toUpperCase())}>
        <Icon name={'equipment/' + equipment} style={{ padding: 0, marginRight: 10, marginBottom: 10 }}/>
      </Tooltip>
    </div>
  }

  renderPreview (h: CreateElement) {
    return <div slot='preview'>
      <ExercisePreview exercise={this.workoutExercise.exercise} />
    </div>
  }

  renderTitle (h: CreateElement) {
    // the exercise falls back to the one of the workout exercise, whose header is rendered before the title
    /* istanbul ignore if */
    if (!this.exercise) return
    const title = exerciseName(this, this.exercise)
    return this.addTooltip ?
      <Tooltip slot='title' content={title}>
        <H6 class='title' ref='title'> {title} </H6>
      </Tooltip>
      : <H6 class='title' slot='title' ref='title'> {title} </H6>
  }

  renderSpecs (h: CreateElement, tempo) {
    return <div slot='specs' class={`specs ${this.isEditable() ? '' : 'view'}`}>
      {this.type === 'default' ? <span><Icon name='util/padlock' class='padlock-icon' /></span> : ''}
      {this.renderWeight(h)}
      {this.renderTempo(h, tempo)}
      {this.renderRepetitions(h)}
      {this.renderPause(h)}
      {this.renderSide(h)}
    </div>
  }

  renderWeight (h: CreateElement) {
    if (this.workoutExercise.exercise.getWeightEquipment() !== '') {
      return <span ref='exercise-details-weight' onClick={this.showWeightConfig}
                   class={`exercise-details ${this.isWeightConfigVisible ? 'selected' : ''}`}>
        <Subtitle2>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + this.workoutExercise.exercise.getWeightEquipment().toUpperCase())}
          {this.workoutExercise.weight ? <b>{this.workoutExercise.weight}{this.$t(`UNITS.${this.weightUnit.toUpperCase()}`)}</b> : null}
        </Subtitle2>
      </span>
    }
  }

  renderTempo (h: CreateElement, tempo: any) {
    if (this.workoutExercise.exercise.hasTempo) {
      return <span ref='exercise-details-tempo' onClick={this.showTempoConfig}
                   class={`exercise-details ${this.isTempoConfigVisible ? 'selected' : ''}`}>
        <Subtitle2>
          {this.$t('TEMPO.TITLE')}
          <b>{tempo.percentage}%</b>
        </Subtitle2>
      </span>
    }
  }

  renderRepetitions (h: CreateElement) {
    if (this.club) {
      return <span ref='exercise-details-repetitions'
                   class={`exercise-details ${this.isRepetitionsConfigVisible ? 'selected' : ''}`}
                   onClick={this.showRepetitionsConfig}>
        <Subtitle2>
        {this.$t('UNITS.REP')}
          <b>{this.computeRepetitions()}</b>
        </Subtitle2>
      </span>
    }
  }

  renderPause (h: CreateElement) {
    let padlock = (this.isCircleWorkout || this.workoutType === WorkoutType.HOME) ?
      <Icon name='util/padlock' class='padlock-icon' />
      : ''

    return <span ref='exercise-details-pause'
                 class={`exercise-details ${this.isPauseConfigVisible ? 'selected' : ''} ${(this.isCircleWorkout || this.workoutType === WorkoutType.HOME) ? 'disabled' : ''}`}
                 onClick={this.showPauseConfig}>
      <Subtitle2>
      {this.$t('PAUSE')}
        <b>{this.workoutExercise.getSubsequentRestPeriod(this.workoutType, this.club)}{this.$t('UNITS.S')}</b>
      </Subtitle2>
      {this.type === 'config' ? padlock : ''}
    </span>
  }

  renderSide (h: CreateElement) {
    if (this.workoutExercise.exercise.hasSides) {
      return <span ref='exercise-details-side' onClick={this.showSideConfig}
                   class={`exercise-details ${this.isSideConfigVisible ? 'selected' : ''}`}>
    <Subtitle2>{this.$t('BODY_SIDE.SIDE')}
      <b>{this.workoutExercise.side ? this.$t(`BODY_SIDE.${this.workoutExercise.side.toUpperCase()}`) : this.$t('BODY_SIDE.ALTERNATING')}</b></Subtitle2>
    </span>
    }
  }

  renderRight (h: CreateElement) {
    const limitationTags = this.exTags['ExerciseTags::HealthLimitation']
    const limits = this.exercise && limitationTags ? this.exercise.getHealthLimitations(limitationTags).notAllowed : []
    const showHealthWarning = this.healthLimitations ? healthCheck(this.healthLimitations, limits) : false
    return <div slot='right' class='right'>
      <div class='column'>
        {this.type === 'config' ?
          <Tooltip content={this.$t('REMOVE')}><Icon name='/util/minus' class='remove-btn' click={() => this.removeExercise(this.index)} /></Tooltip> : ''}
        {this.type !== 'strictView' ? <Tooltip content={this.$t('VIEW_DETAILS')}><Icon name='/util/info' class='info-btn' click={this.showHideInfo} /></Tooltip> : ''}
        {showHealthWarning && <Tooltip content={this.$t('HEALTH_LIMITATIONS_WARNING')}><Icon name='/util/warning' class='info-btn' /></Tooltip> }
      </div>
      {this.type === 'config' ?
        <div class='column'><PositionChanger total={this.selectedWorkoutExercises.length} position={this.index + 1}
                                             changePosition={this.changePosition} /></div> : ''}
      {this.showExerciseInfo ?
        <ExerciseInfoModal exercise={this.workoutExercise.exercise}
                           showModal={this.showExerciseInfo}
                           closeCallback={this.showHideInfo} /> : null}
    </div>
  }

  renderWeightConfig (h: CreateElement) {
    if (this.isWeightConfigVisible && this.isEditable()) {
      return <div slot='expanded' class='expanded'>
        <Tooltip content={this.$t('RESET')}><Icon name='/util/reset-arrow' class='refresh'
                                                  click={() => this.changeWeight(this.workoutExercise.exercise.getDefaultWeight())} /></Tooltip>
        <Input type={'decimal'} minValue={0.1} maxValue={100}
               initialValue={this.workoutExercise.weight ? this.workoutExercise.weight.toString() : '1'}
               valueChanged={(value) => this.changeWeight(value)}
               key='weightInput'
               label={this.weightUnit} />
        <Tooltip content={this.$t('COLLAPSE')}><Icon name='util/caret-up' class='caret-up-btn'
                                                     click={this.closeConfig} /></Tooltip>
      </div>
    }
  }

  renderTempoConfig (h: CreateElement, tempo: any) {
    if (this.isTempoConfigVisible && this.isEditable()) {
      return <div slot='expanded' class='expanded'>
        <Tooltip content={this.$t('RESET')}><Icon name='/util/reset-arrow' class='refresh' click={() =>
          this.changeTempo(this.workoutExercise.initialExerciseConfig!.repetitionDuration || this.workoutExercise.exercise.durationMedium)} /></Tooltip>
        <Tooltip content={this.$t('TEMPO.FASTER')}><Icon name='workout/tempo-plus' class='tempo-minus'
                                                         click={() => this.minusTempo(tempo)} /></Tooltip>
        <Input type={'number'} minValue={0}
               readonly={true}
               value={this.workoutExercise.repetitionDuration.toFixed(2).toString()}
               key='tempoInput'
               label={this.$t('SECONDS')} />
        <Tooltip content={this.$t('TEMPO.SLOWER')}><Icon name='workout/tempo-minus' class='tempo-plus'
                                                         click={() => this.plusTempo(tempo)} /></Tooltip>
        <Tooltip content={this.$t('COLLAPSE')}><Icon name='util/caret-up' class='caret-up-btn'
                                                     click={this.closeConfig} /></Tooltip>
      </div>
    }
  }

  minusTempo (tempo) {
    if (this.isCircleWorkout) {
      let nextRepetitions = this.workoutExercise.getRepetitionsCount(this.club.screenExerciseLen) + 1
      if (this.repetitionsValid(nextRepetitions)) {
        this.changeRepetitions(nextRepetitions)
      }
      return
    }
    const newTempo = this.workoutExercise.repetitionDuration - tempo.sliderStep
    if (newTempo >= tempo.minRepetitionDuration) {
      this.changeTempo(newTempo)
    } else {
      this.changeTempo(tempo.minRepetitionDuration)
    }
  }

  plusTempo (tempo) {
    if (this.isCircleWorkout) {
      let nextRepetitions = this.workoutExercise.getRepetitionsCount(this.club.screenExerciseLen) - 1
      if (this.repetitionsValid(nextRepetitions)) {
        this.changeRepetitions(nextRepetitions)
      }
      return
    }
    const newTempo = this.workoutExercise.repetitionDuration + tempo.sliderStep
    if (newTempo <= tempo.maxRepetitionDuration) {
      this.changeTempo(newTempo)
    } else {
      this.changeTempo(tempo.maxRepetitionDuration)
    }
  }

  plusReps () {
    if (this.isCircleWorkout) {
      let nextRepetitions = this.workoutExercise.getRepetitionsCount(this.club.screenExerciseLen) + 1
      if (this.repetitionsValid(nextRepetitions)) {
        this.changeRepetitions(nextRepetitions)
      }
      return
    }
    const newReps = this.computeRepetitions() + 1
    if (newReps <= this.maxReps) {
      this.changeRepetitions(newReps)
    }
  }
  minusReps () {
    if (this.isCircleWorkout) {
      let nextRepetitions = this.workoutExercise.getRepetitionsCount(this.club.screenExerciseLen) - 1
      if (this.repetitionsValid(nextRepetitions)) {
        this.changeRepetitions(nextRepetitions)
      }
      return
    }
    const newReps = this.computeRepetitions() - 1
    if (newReps >= this.minReps) {
      this.changeRepetitions(newReps)
    }
  }

  repetitionsValid (reps) {
    let initialRepetitionDuration = this.workoutExercise.exercise.durationMedium
    let percentage = ((100 / initialRepetitionDuration) * (this.club.screenExerciseLen / reps)) - 100

    return Math.abs(percentage) <= ALLOWED_TEMPO_ADJUSTMENT_PERCENTAGE
  }

  renderRepetitionsConfig (h: CreateElement) {
    if (this.isRepetitionsConfigVisible && this.isEditable()) {
      return <div slot='expanded' class='expanded'>
        <Tooltip content={this.$t('RESET')}><Icon name='/util/reset-arrow' class='refresh'
                                                  click={this.resetRepetitions} /></Tooltip>
        <Tooltip content={this.$t('REPS.LESS')}><Icon name='workout/tempo-minus' class='tempo-minus'
                                                         click={() => this.minusReps()} /></Tooltip>
        <Input type={'number'} minValue={this.minReps} maxValue={this.maxReps} defaultValue={this.defaultReps}
               value={this.computeRepetitions().toString()}
               valueChanged={(value) => this.changeRepetitions(value)}
               key='repetitionsInput'
               label={this.$t('REPETITIONS')} />
        <Tooltip content={this.$t('REPS.MORE')}><Icon name='workout/tempo-plus' class='tempo-plus'
                                                         click={() => this.plusReps()} /></Tooltip>
        <Tooltip content={this.$t('COLLAPSE')}><Icon name='util/caret-up' class='caret-up-btn'
                                                     click={this.closeConfig} /></Tooltip>
      </div>
    }
  }

  renderPauseConfig (h: CreateElement) {
    if (this.isPauseConfigVisible && !this.isCircleWorkout && this.isEditable()) {
      return <div slot='expanded' class='expanded'>
        <Tooltip content={this.$t('RESET')}>
          <Icon name='/util/reset-arrow' class='refresh'
                click={() => this.changePause(this.workoutExercise.initialExerciseConfig!.getSubsequentRestPeriod(this.workoutType, this.club))} /></Tooltip>
        <Input type={'number'} minValue={1} maxValue={MAXIMUM_EXERCISE_PAUSE} defaultValue={this.workoutExercise.initialExerciseConfig!.getSubsequentRestPeriod(this.workoutType, this.club).toString()}
               value={this.workoutExercise.getSubsequentRestPeriod(this.workoutType, this.club).toString()}
               valueChanged={(value) => this.changePause(value)}
               key='pauseInput'
               label={this.$t('SECONDS')} />
        <Tooltip content={this.$t('COLLAPSE')}><Icon name='util/caret-up' class='caret-up-btn'
                                                     click={this.closeConfig} /></Tooltip>
      </div>
    }
  }

  renderSideConfig (h: CreateElement) {
    if (this.isSideConfigVisible && this.isEditable()) {
      return <div slot='expanded' class='expanded'>
        <Tooltip content={this.$t('RESET')}><Icon name='/util/reset-arrow' class='refresh'
                                                  click={() => this.changeSide(this.workoutExercise.initialExerciseConfig!.side)} /></Tooltip>
        <el-radio-group class='exercise-config-radio'>
          <Radio value='' text={this.$t('BODY_SIDE.ALTERNATING')} bindedProp={this.workoutExercise.side}
                 change={this.changeSide} />
          <Radio value='left' text={this.$t('BODY_SIDE.LEFT')} bindedProp={this.workoutExercise.side}
                 change={this.changeSide} />
          <Radio value='right' text={this.$t('BODY_SIDE.RIGHT')} bindedProp={this.workoutExercise.side}
                 change={this.changeSide} />
        </el-radio-group>
        <Tooltip content={this.$t('COLLAPSE')}><Icon name='util/caret-up' class='caret-up-btn'
                                                     click={this.closeConfig} /></Tooltip>
      </div>
    }
  }

  renderExerciseBreakPoint (h: CreateElement) {
    if (!this.showBreakpointOption) return ''

    const addIcon = <Icon name={'/util/plus'} class='info-btn add' click={this.breakpointToggle} />
    const removeIcon = <Icon name={'/util/minus'} class='info-btn remove' click={this.breakpointToggle} />
    return <div slot='specs' class='exercise-breakpoint'>
      {this.type === 'config' ?
        <Tooltip content={this.workoutExercise.breakpointAfterExercise ? this.$t('PAGE.CONTENT.WORKOUT.BREAKPOINT_REMOVE') : this.$t('PAGE.CONTENT.WORKOUT.BREAKPOINT_ADD')}>
          {this.workoutExercise.breakpointAfterExercise ? removeIcon : addIcon}
        </Tooltip> : ''}
      {this.workoutExercise.breakpointAfterExercise ? <Tooltip content={this.$t('PAGE.CONTENT.WORKOUT.BREAKPOINT')}><hr/></Tooltip> : ''}
    </div>
  }

  showWeightConfig () {
    this.isWeightConfigVisible = !this.isWeightConfigVisible
    this.isTempoConfigVisible = false
    this.isRepetitionsConfigVisible = false
    this.isPauseConfigVisible = false
    this.isSideConfigVisible = false
  }

  showTempoConfig () {
    this.isTempoConfigVisible = !this.isTempoConfigVisible
    this.isWeightConfigVisible = false
    this.isRepetitionsConfigVisible = false
    this.isPauseConfigVisible = false
    this.isSideConfigVisible = false
  }

  showRepetitionsConfig () {
    this.isRepetitionsConfigVisible = !this.isRepetitionsConfigVisible
    this.isWeightConfigVisible = false
    this.isTempoConfigVisible = false
    this.isPauseConfigVisible = false
    this.isSideConfigVisible = false
  }

  showPauseConfig () {
    if (!this.isCircleWorkout && this.workoutType !== WorkoutType.HOME) {
      // do not open the pause config for circle workouts
      this.isPauseConfigVisible = !this.isPauseConfigVisible
      this.isWeightConfigVisible = false
      this.isTempoConfigVisible = false
      this.isRepetitionsConfigVisible = false
      this.isSideConfigVisible = false
    }
  }

  showSideConfig () {
    this.isSideConfigVisible = !this.isSideConfigVisible
    this.isPauseConfigVisible = false
    this.isWeightConfigVisible = false
    this.isTempoConfigVisible = false
    this.isRepetitionsConfigVisible = false
  }

  @Watch('workoutExercise.exerciseId')
  closeConfig () {
    this.isTempoConfigVisible = false
    this.isWeightConfigVisible = false
    this.isRepetitionsConfigVisible = false
    this.isPauseConfigVisible = false
    this.isSideConfigVisible = false
  }

  showHideInfo () {
    this.showExerciseInfo = !this.showExerciseInfo
  }

  breakpointToggle () {
    this.$store.dispatch('changeExerciseBreakpoint', { index: this.index, value: !this.workoutExercise.breakpointAfterExercise })
  }

  changeWeight (value: number) {
    this.$store.dispatch('changeExerciseWeight', { index: this.index, weight: value })
  }

  changeRepetitionsCount (repetitions: number | null) {
    this.$store.dispatch('changeRepetitionsCount', { index: this.index, repetitions })
  }

  changePause (subsequentRestPeriod: number) {
    if (subsequentRestPeriod > MAXIMUM_EXERCISE_PAUSE) return
    this.$store.dispatch('changeSubsequentRestPeriod', { index: this.index, subsequentRestPeriod })
  }

  changeSide (side: string | null) {
    this.$store.dispatch('changeSide', { index: this.index, side })
  }

  changeExerciseDuration (repetitionDuration: number) {
    this.$store.dispatch('changeExerciseDuration', { index: this.index, repetitionDuration })
  }

  changeTempo (value: number) {
    this.changeRepetitionDuration(value)
  }

  changeRepetitions (repetitionsCount: number) {
    this.maxReps = this.originalMaxReps
    if (!repetitionsCount || !this.isEditable()) {
      // THIS IS IMPORTANT!!!
      // In order to allow the user to delete the entire content of the reps input, the value must not be updated with null because of how
      // this.workoutExercise.getRepetitionsCount method works: if repetitions is null or 0, it will automatically return the default
      // value, populating the input with another value.
      return
    }
    if (this.isCircleWorkout) {
      const tempo = this.computeTempo()
      this.minReps = Math.ceil(this.club.screenExerciseLen / tempo.maxRepetitionDuration)
      this.maxReps = Math.floor(this.club.screenExerciseLen / tempo.minRepetitionDuration)
      // for circle workouts, set the duration of a repetition, based on the exercise duration (club setting) and the desired number of repetition (the tempo changes)
      // exercises without a tempo only have the repetition duration set (no repetition count)
      repetitionsCount = repetitionsCount < this.minReps ? this.minReps : repetitionsCount > this.maxReps ? this.maxReps : repetitionsCount
      this.changeRepetitionDuration(+parseFloat(`${this.club.screenExerciseLen / repetitionsCount}`))
    } else {
      this.minReps = this.originalMinReps
      if (repetitionsCount > this.maxReps) {
        repetitionsCount = this.maxReps
      }
      // for continuous/non-continuous workouts, the repetitions number is set (tempo does not change)
      this.changeRepetitionsCount(repetitionsCount)
    }
  }

  changeRepetitionDuration (repetitionDuration) {
    // the repetition duration cannot exceed the allowed interval
    this.changeExerciseDuration(repetitionDuration)
  }

  updateRepetitionsBounders () {
    if (this.isCircleWorkout) {
      let tempo = this.computeTempo()
      this.minReps = Math.ceil(this.club.screenExerciseLen / tempo.maxRepetitionDuration)
      this.maxReps = Math.floor(this.club.screenExerciseLen / tempo.minRepetitionDuration)
    } else {
      this.minReps = 1
      this.maxReps = 1000
    }
    this.changeRepetitions(this.computeRepetitions())
  }

  computeTempo () {
    const initialRepetitionDuration = this.workoutExercise.exercise.durationMedium
    let percentage = Math.round(((100 / initialRepetitionDuration) * this.workoutExercise.repetitionDuration) - 100) * -1
    if (this.isCircleWorkout && Math.abs(percentage) > 35) {
      percentage = percentage > 0 ? initialRepetitionDuration * ALLOWED_TEMPO_ADJUSTMENT_PERCENTAGE / 100 : initialRepetitionDuration * -ALLOWED_TEMPO_ADJUSTMENT_PERCENTAGE / 100
      this.workoutExercise.repetitionDuration = initialRepetitionDuration * (1 + (percentage / 100))
    }

    return {
      minRepetitionDuration: initialRepetitionDuration - (initialRepetitionDuration * ALLOWED_TEMPO_ADJUSTMENT_PERCENTAGE / 100),
      maxRepetitionDuration: initialRepetitionDuration + (initialRepetitionDuration * ALLOWED_TEMPO_ADJUSTMENT_PERCENTAGE / 100),
      sliderStep: Math.floor(initialRepetitionDuration) / 100,
      percentage: percentage > 0 ? '+' + percentage : percentage
    }
  }

  resetTiming () {
    // reset repetitions
    this.changeRepetitionsCount(null)
    this.changePause(0)
  }

  setExerciseConfigRepetitions () {
    if (this.workoutExercise.repetitions === null) {
      this.changeRepetitionsCount(+parseFloat(`${this.club.screenExerciseLen / this.workoutExercise.repetitionDuration}`).toFixed(2))
    }
  }

  resetRepetitions () {
    if (!this.isCircleWorkout) {
      this.changeRepetitionsCount(this.workoutExercise.initialExerciseConfig!.repetitions)
    } else {
      this.changeRepetitionDuration(this.workoutExercise.initialExerciseConfig!.repetitionDuration)
    }
  }

  removeExercise (index: number) {
    this.$store.dispatch('removeExerciseFromWorkout', index)
  }

  changePosition (newPosition: number) {
    this.$store.dispatch('changeExercisePosition', { index1: this.index, index2: newPosition - 1 })
  }

  @Watch('isCircleWorkout')
  onWorkoutTypeChange (newVal: boolean, oldVal: boolean) {
    this.updateRepetitionsBounders()
    if (!newVal && oldVal) {
      this.workoutExercise.breakpointAfterExercise = false
    }
  }

  @Watch('isContinuousWorkout')
  onContinuousWorkoutChange (newVal: boolean, oldVal: boolean) {
    if (!newVal && oldVal) {
      this.workoutExercise.breakpointAfterExercise = false
    }
  }
}
